import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import i18n from "i18next";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { faArrowsRotate, faTimes } from "@fortawesome/pro-regular-svg-icons";
import Typography from "@mui/material/Typography";
import React from "react";
import { ConfiguratorFormFooterPriceView } from "src/components/views/erp/configurator/configuratorForm/components/ConfiguratorFormFooterPriceView.tsx";
import { Box } from "@mui/material";
import { ResetConfiguratorValuesButton } from "src/components/views/erp/configurator/configuratorForm/components/ResetConfiguratorValuesButton.tsx";
import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel.ts";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";

export interface ConfiguratorTreeFooterProps {
	rootConfiguratorItem: ConfiguratorTreeItemModel;
	currentInputVersion: number;
	onCancel?: () => void;
	onValidate?: () => Promise<unknown>;
	resetToExistingConfiguration?: () => Promise<unknown>;
	resetToDefaultValues?: () => Promise<unknown>;
	updateVersions?: () => Promise<unknown>;
	submitForm?: () => Promise<unknown>;
	hasUnsavedChanges: boolean;
	extraComponents?: (params: { rootConfiguratorItem: ConfiguratorTreeItemModel }) => React.ReactNode;
	readonly: boolean;
}

export const ConfiguratorTreeFooter = (props: ConfiguratorTreeFooterProps) => {
	const {
		rootConfiguratorItem,
		currentInputVersion,
		onCancel,
		onValidate,
		submitForm,
		resetToExistingConfiguration,
		resetToDefaultValues,
		updateVersions,
		hasUnsavedChanges,
		extraComponents,
		readonly,
	} = props;

	const showConfirmDialog = useConfirmDialog();

	return (
		<AavoFormFooter
			sx={{
				paddingLeft: 1,
			}}
		>
			{!readonly && (
				<ConfiguratorFormFooterPriceView
					rootConfiguratorItem={rootConfiguratorItem}
					currentInputVersion={currentInputVersion}
				/>
			)}
			{hasUnsavedChanges ?
				<Typography
					sx={{
						color: "warning.main",
						alignSelf: "center",
						flex: 1,
						textAlign: "center",
					}}
				>
					{i18n.t("configurator_has_unsaved_changes_message")}
				</Typography>
			:	<Box
					sx={{
						flex: 1,
					}}
				/>
			}
			{extraComponents?.({ rootConfiguratorItem })}
			{!readonly && updateVersions && (
				<AsyncButton
					color={"secondary"}
					label={i18n.t("update_versions")}
					icon={faArrowsRotate}
					onClick={onUpdateVersionsClicked}
				/>
			)}
			{!readonly && (
				<ResetConfiguratorValuesButton
					resetToExistingConfiguration={resetToExistingConfiguration}
					resetToDefaultValues={resetToDefaultValues}
					color={"secondary"}
				/>
			)}
			{onValidate && (
				<AsyncButton
					color={"secondary"}
					label={i18n.t("validate")}
					icon={faCircleCheck}
					onClick={async () => {
						await onValidate();
					}}
				/>
			)}
			{onCancel && (
				<AsyncButton
					color={"secondary"}
					label={i18n.t("close")}
					icon={faTimes}
					onClick={async () => {
						onCancel();
					}}
				/>
			)}
			{submitForm && !readonly && (
				<AsyncButton
					label={i18n.t("ok")}
					onClick={async () => {
						await submitForm();
					}}
				/>
			)}
		</AavoFormFooter>
	);

	async function onUpdateVersionsClicked() {
		if (!updateVersions) return;

		const confirmed = await showConfirmDialog({
			message: i18n.t("update_versions_confirmation"),
		});
		if (!confirmed) return;

		await updateVersions();
	}
};
